// PlanComponents/FunnelVisualizationCard.js
import React from 'react';
import { Typography, Box, Chip, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import { bg, text as textColors, border } from '../../../utils/darkModeAdapter';

import CardItem from './CardItem';
import AdviceContentBox from '../../../components/AdviceContentBox';

// 日本市場実績（マイナビ中途採用状況調査2025年版）
const JAPAN_ACTUALS = {
  stages: [
    { label: '応募', count: 79.5 },
    { label: '面接設定', count: 43.9 },
    { label: '内定', count: 22.9 },
    { label: '採用', count: 20.8 },
  ],
  source: 'マイナビ 中途採用状況調査2025年版'
};

// ファネルステージごとの色グラデーション
const STAGE_COLORS = [
  '#42a5f5', // 応募 (水色)
  '#66bb6a', // 書類通過 (緑)
  '#26a69a', // 一次面接 (ティール)
  '#ab47bc', // 最終面接 (紫)
  '#4caf50', // 内定承諾 (濃緑)
];

/**
 * 採用ファネル可視化カード
 * 科学的根拠: SHRM Yield Ratio公式, マイナビ中途採用状況調査2025年版, Gem 2025
 */
export default function FunnelVisualizationCard({ funnelData, targetHires, requiredApplicants, adv }) {
  const theme = useTheme();

  if (!funnelData || !funnelData.stages || funnelData.stages.length === 0) {
    return (
      <CardItem title="採用ファネル" adviceContent={adv?.recommendedHiresAndApplicants ? <AdviceContentBox adviceObj={adv.recommendedHiresAndApplicants} /> : null}>
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
          ファネルデータがありません
        </Typography>
      </CardItem>
    );
  }

  const { stages, confidenceLevel, dataSource } = funnelData;
  const maxCount = stages[0]?.count || 1;

  return (
    <CardItem title="採用ファネル" adviceContent={adv?.recommendedHiresAndApplicants ? <AdviceContentBox adviceObj={adv.recommendedHiresAndApplicants} /> : null}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* ファネル図 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {stages.map((stage, index) => {
            const widthPercent = Math.max(10, (stage.count / maxCount) * 100);
            const color = STAGE_COLORS[index % STAGE_COLORS.length];

            return (
              <Box key={stage.stage} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* ステージ名 */}
                <Box sx={{ width: 100, flexShrink: 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: textColors(theme).secondary, whiteSpace: 'nowrap' }}>
                    {stage.stage}
                  </Typography>
                </Box>

                {/* バー */}
                <Box sx={{ flex: 1, position: 'relative' }}>
                  <Box
                    sx={{
                      width: `${widthPercent}%`,
                      height: 28,
                      borderRadius: 1.5,
                      backgroundColor: color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      pr: 1,
                      transition: 'width 0.5s ease-out',
                      minWidth: 40,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem' }}>
                      {stage.count}名
                    </Typography>
                  </Box>
                </Box>

                {/* 通過率 */}
                <Box sx={{ width: 80, flexShrink: 0, textAlign: 'right' }}>
                  {stage.rate !== null && stage.rate !== undefined ? (
                    <Tooltip title={stage.benchmark ? `ベンチマーク: ${stage.benchmark}` : ''} arrow placement="left">
                      <Typography variant="caption" sx={{ fontWeight: 600, color: textColors(theme).muted }}>
                        通過率 {stage.rate}%
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" sx={{ color: textColors(theme).muted }}>—</Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* 日本市場実績比較 */}
        <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: textColors(theme).secondary }}>
              日本市場平均実績（参考）
            </Typography>
            <Tooltip title="歩留まり率 = 次段階通過者数 ÷ 前段階人数 × 100" arrow>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: textColors(theme).muted, cursor: 'help' }} />
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {JAPAN_ACTUALS.stages.map((s, i) => (
              <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <Typography variant="caption" sx={{ color: textColors(theme).muted }}>
                  {s.label}: <strong>{s.count}名</strong>
                </Typography>
                {i < JAPAN_ACTUALS.stages.length - 1 && (
                  <Typography variant="caption" sx={{ color: textColors(theme).muted, mx: 0.3 }}>→</Typography>
                )}
              </Box>
            ))}
          </Box>
          <Typography variant="caption" sx={{ color: textColors(theme).muted, display: 'block', mt: 0.5 }}>
            応募→面接設定率 55.2% / 面接→内定率 52.2% / 内定辞退率 9.3%
          </Typography>
        </Box>

        {/* メタ情報チップ */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {confidenceLevel && (
            <Chip
              label={`信頼度: ${confidenceLevel}`}
              size="small"
              variant="outlined"
              sx={{
                borderColor: confidenceLevel === '高' ? '#4caf50' : confidenceLevel === '中' ? '#ff9800' : '#f44336',
                color: confidenceLevel === '高' ? '#4caf50' : confidenceLevel === '中' ? '#ff9800' : '#f44336',
                fontWeight: 600,
                fontSize: '0.7rem'
              }}
            />
          )}
          {dataSource && (
            <Chip
              label={`データ: ${dataSource}`}
              size="small"
              variant="outlined"
              sx={{ borderColor: border(theme).default, color: textColors(theme).muted, fontSize: '0.7rem' }}
            />
          )}
        </Box>

        {/* 出典 */}
        <Typography variant="caption" sx={{ color: textColors(theme).muted, fontStyle: 'italic' }}>
          ※ ファネル通過率は日本市場調査およびグローバル採用統計に基づく
        </Typography>
      </Box>
    </CardItem>
  );
}
